// The feed-row type idioms: pending (outline pill), split (ink-filled
// count), bank-amended (catalog chip), removed (struck-through caption),
// income/transfer/cc_payment/recurring (labelled pill). Colour is never
// the only signal -- every kind carries a word (§1.2/§1.3). Chip copy is
// the TYPE_BADGE_LABEL catalog only; never interpolate formatted amounts
// (ui-conventions §8).
import { Badge } from '@moneyskyline/ui';

export type TypeBadgeKind =
  | 'income'
  | 'transfer'
  | 'cc_payment'
  | 'recurring'
  | 'pending'
  | 'split'
  | 'amended'
  | 'removed';

export const TYPE_BADGE_LABEL: Record<TypeBadgeKind, string> = {
  income: 'Income',
  transfer: 'Transfer',
  cc_payment: 'Card payment',
  recurring: 'Recurring',
  pending: 'Pending',
  split: 'Split',
  amended: 'Bank amended',
  removed: 'Removed by bank',
};

/** §14 decision 4: the Pending badge's colour follows the row's money colour -- red for an expense row, green for income -- never grey. `tone` is omitted when the row's sign is not yet resolvable (e.g. a transfer), which keeps the neutral outline. */
export type TypeBadgeTone = 'expense' | 'income';

const PENDING_TONE_CLASS: Record<TypeBadgeTone, string> = {
  expense: 'border-expense text-expense',
  income: 'border-income text-income',
};

export function TypeBadge({
  kind,
  detail,
  tone,
  className,
}: {
  kind: TypeBadgeKind;
  detail?: string;
  /** Only meaningful for `kind="pending"` -- see `TypeBadgeTone`. */
  tone?: TypeBadgeTone;
  className?: string;
}) {
  const cls = ['rounded-full font-normal', className].filter(Boolean).join(' ');

  if (kind === 'pending') {
    return (
      <Badge
        variant="outline"
        className={[cls, tone ? PENDING_TONE_CLASS[tone] : ''].filter(Boolean).join(' ')}
        data-testid="type-badge"
        data-kind={kind}
        data-tone={tone}
      >
        {TYPE_BADGE_LABEL.pending}
      </Badge>
    );
  }
  if (kind === 'split') {
    return (
      <Badge
        className={['rounded-full bg-primary font-normal text-primary-foreground', className].filter(Boolean).join(' ')}
        data-testid="type-badge"
        data-kind={kind}
      >
        {TYPE_BADGE_LABEL.split}{detail ? ` ×${detail}` : ''}
      </Badge>
    );
  }
  if (kind === 'amended') {
    return (
      <Badge variant="secondary" className={cls} data-testid="type-badge" data-kind={kind}>
        {TYPE_BADGE_LABEL.amended}
      </Badge>
    );
  }
  if (kind === 'removed') {
    return (
      <span
        className={['text-xs text-muted-foreground line-through', className].filter(Boolean).join(' ')}
        data-testid="type-badge"
        data-kind={kind}
      >
        {TYPE_BADGE_LABEL.removed}
        {detail ? ` · ${detail}` : ''}
      </span>
    );
  }
  if (kind === 'transfer' || kind === 'cc_payment') {
    return (
      <Badge
        variant="secondary"
        className={['rounded-full font-normal text-money-neutral', className].filter(Boolean).join(' ')}
        data-testid="type-badge"
        data-kind={kind}
      >
        {'⇄ '}
        {TYPE_BADGE_LABEL[kind]}
        {detail ? ` · ${detail}` : ''}
      </Badge>
    );
  }

  return (
    <Badge variant="secondary" className={cls} data-testid="type-badge" data-kind={kind}>
      {TYPE_BADGE_LABEL[kind]}
      {detail ? ` · ${detail}` : ''}
    </Badge>
  );
}

export default TypeBadge;
